import { useEffect } from 'react';
import $ from 'jquery';
import { useBookingStore } from '../store/useBookingStore';
import { useService } from './useBookingComputed';
import { updateSelectOptions } from '../utils/fieldRenderers';

// Mirrors the server-side fallbacks in GP_Bookings\Fields\Occupancy.
const DEFAULT_MIN = 1;
const DEFAULT_CHOICE_MAX = 10;

/**
 * Hook to keep the Occupancy field's allowed range in sync with the selected
 * service.
 *
 * The min/max are rendered server-side from the service configured on the
 * Service field, so they are stale (or absent entirely, in manual selection
 * mode) once the user picks a different service.
 */
export function useOccupancyRangeUpdater(): void {
	const formId = useBookingStore((state) => state.formId);
	const occupancyFieldId = useBookingStore((state) => state.occupancyFieldId);
	const { data: service } = useService();

	const min = service?.minOccupancy ?? null;
	const max = service?.maxOccupancy ?? null;

	useEffect(() => {
		if (!formId || !occupancyFieldId || !service) {
			return;
		}

		const $field = $(`#field_${formId}_${occupancyFieldId}`);
		if (!$field.length) {
			return;
		}

		const $number = $field.find('input[type="number"]');
		if ($number.length) {
			updateNumberRange($number, min, max);
			return;
		}

		const $select = $field.find('select');
		if ($select.length) {
			const placeholder = $select.find('option[value=""]').text();
			updateSelectOptions($select, buildChoices(min, max), placeholder);
			return;
		}

		const $radioContainer = $field.find('.gfield_radio');
		if ($radioContainer.length) {
			updateRadioRange(
				$radioContainer,
				buildChoices(min, max),
				formId,
				occupancyFieldId
			);
		}
	}, [formId, occupancyFieldId, service, min, max]);
}

function buildChoices(min: number | null, max: number | null) {
	const from = min ?? DEFAULT_MIN;
	const to = max ?? Math.max(from, DEFAULT_CHOICE_MAX);

	const choices = [];
	for (let value = from; value <= to; value++) {
		choices.push({ id: value, name: String(value) });
	}

	return choices;
}

/**
 * Apply min/max attributes to a number input, clamping the current value into
 * the new range.
 */
function updateNumberRange(
	$number: JQuery,
	min: number | null,
	max: number | null
): void {
	const lower = min ?? DEFAULT_MIN;

	$number.attr('min', lower);
	if (max === null) {
		$number.removeAttr('max');
	} else {
		$number.attr('max', max);
	}

	const current = parseInt(String($number.val() ?? ''), 10);
	if (isNaN(current)) {
		return;
	}

	const clamped = Math.min(Math.max(current, lower), max ?? current);
	if (clamped === current) {
		return;
	}

	$number.val(clamped);

	const el = $number.get(0);
	if (el) {
		el.dispatchEvent(new Event('change', { bubbles: true }));
	}
}

/**
 * Rebuild an occupancy radio group, preserving the selection when it is still
 * within the new range.
 *
 * Kept separate from `updateRadioButtons` so the markup matches what the
 * Occupancy field renders server-side (`div.gchoice`, not `li.gchoice`).
 */
function updateRadioRange(
	$radioContainer: JQuery,
	choices: Array<{ id: number; name: string }>,
	formId: number,
	fieldId: number
): void {
	const currentValue = $radioContainer
		.find('input[type="radio"]:checked')
		.val();
	const valueToSet = choices.some(
		(choice) => String(choice.id) === String(currentValue)
	)
		? String(currentValue)
		: null;

	$radioContainer.empty();

	choices.forEach((choice, index) => {
		const inputId = `choice_${formId}_${fieldId}_${index}`;
		const $choice = $('<div>', {
			class: `gchoice gchoice_${formId}_${fieldId}_${index}`,
		});

		const $input = $('<input>', {
			class: 'gfield-choice-input',
			type: 'radio',
			name: `input_${fieldId}`,
			id: inputId,
			value: String(choice.id),
		});

		if (valueToSet === String(choice.id)) {
			$input.prop('checked', true);
		}

		const $label = $('<label>', {
			for: inputId,
			id: `label_${formId}_${fieldId}_${index}`,
			class: 'gform-field-label gform-field-label--type-inline',
			text: choice.name,
		});

		$choice.append($input, $label);
		$radioContainer.append($choice);
	});

	const el = $radioContainer.get(0);
	if (el) {
		el.dispatchEvent(new Event('change', { bubbles: true }));
	}
}
